import React, { useEffect, useState } from "react";
import authStore from "@/stores/authStore";
import { observer } from "mobx-react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import popupService from "@/services/popupService";
import api from "@/services/api";
import { useTranslation } from "react-i18next";
import "./login.css";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import LoginPageIcon from "@/assets/images/LoginPageIcon.png";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import LaluneLogo from "@/assets/images/lalun-logo-login.svg";
import Swal from "sweetalert2";
import { LoginRequest } from "@/models/apiModels";
import CloseIcon from "@/assets/icons/close-icon.svg";
import LoginInputForm from "../styled/loginInputForm";
import LoginButton from "../styled/loginButton";
import { CredentialState } from "@/models/enums";

declare global {
    interface Window {
        grecaptcha: any;
    }
}

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const LoginPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [credentialState, setCredentialState] = useState(CredentialState.Pending);
    const [searchParams] = useSearchParams();
    const [loginData, setLoginData] = useState<LoginRequest>({
        username: "",
        captchaResponse: "",
    });
    const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string | null>(null);

    const checkCredentials = async () => {
        try {
            const res = await api.authInfo();
            setDir(res.data.initResponse.isRtl);
            if (res.data.initResponse.cultureName !== localStorage.getItem("Culture")) {
                localStorage.setItem("Culture", res.data.initResponse.cultureName);
                window.location.reload();
                return;
            }

            setRecaptchaSiteKey(res.data.initResponse.recaptchaSiteKey);

            authStore.update(res.data);
            document.documentElement.lang = res.data.initResponse.cultureTwoLetter;
            setCredentialState(CredentialState.Success);
        } catch {
            setCredentialState(CredentialState.Error);
        }
    };

    const getReturnUrl = () => {
        return searchParams.get("return") ?? "/";
    };

    function setDir(isRtl: boolean) {
        const body = document.querySelector("body");
        const cls = isRtl ? "rtl" : "ltr";
        body?.classList.add(cls);
        body?.setAttribute("dir", cls);
    }

    const usernameValidation = (username: string) => {
        if (!username.length) {
            popupService.showWarning(t("login.UsernameEmpty"));
            return false;
        }
        return true;
    };

    const submitOnClick = async (event: any) => {
        event.preventDefault();
        popupService.showLoading();

        if (usernameValidation(loginData.username)) {
            // Get reCAPTCHA token
            const token = await window.grecaptcha.execute(`${authStore.recaptchaSiteKey}`, {
                action: "submit",
            });

            try {
                // Send token along with login request
                var response = await api.login({ ...loginData, captchaResponse: token });
                if (response.data.userHash) {
                    await loginByCode(response.data.userHash);
                }
            } catch (err) {
                popupService.showApiError(err);
                return;
            }
        }
    };

    const getRecaptchaToken = async () => {
        return await window.grecaptcha.execute(`${authStore.recaptchaSiteKey}`, { action: "submit" });
    };

    const loginByCode = async (userHash: string) => {
        const { value: code } = await Swal.fire({
            width: 450,
            customClass: "login-by-code-dialog",
            title: t("general.LoginCodeMsg").toString(),
            input: "text",
            inputLabel: t("general.Code").toString(),
            showCancelButton: true,
            showCloseButton: true,
            closeButtonHtml: `<img src=${CloseIcon} />`,
            cancelButtonText: t("button.cancel").toString(),
            confirmButtonText: t("button.send").toString(),
            reverseButtons: true,
            inputValidator: (value) => {
                if (!value) {
                    return t("general.CodeEmpty").toString();
                }
                return "";
            },
            preConfirm: async (code) => {
                if (code) {
                    try {
                        const token = await getRecaptchaToken();
                        await api.loginByCode({ code: code, userHash: userHash, captchaResponse: token });
                    } catch (err: any) {
                        const [errorMessage] = api.getFirstError(err);
                        Swal.showValidationMessage(errorMessage);
                        return false; // prevent closing the popup
                    }

                    try {
                        const res = await api.authInfo();
                        authStore.update(res.data);
                    } catch {
                        popupService.showError("Error checking credentials");
                        return;
                    }
                }
            },
        });

        if (code) {
            popupService.close();
            const returnUrl = getReturnUrl();
            navigate(returnUrl);
        }
    };

    useEffect(() => {
        checkCredentials();
    }, []);

    useEffect(() => {
        if (recaptchaSiteKey) {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
            script.async = true;
            document.body.appendChild(script);

            document.body.classList.add("hide-grecaptcha-badge");
        }
    }, [recaptchaSiteKey]);

    const renderForm = () => {
        return (
            <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
                <div className="login-main-page">
                    <div className="login-left-side">
                        <div className="login-header">
                            <div className="login-header-wello">
                                <img src={LaluneLogo} alt="LaluneLogo" style={{ maxWidth: 121, width: "100%" }} />
                            </div>
                            <div className="login-header-logo">Logo</div>
                        </div>
                        <form className="login-form" onSubmit={submitOnClick}>
                            <div className="login-text">
                                <div>{t("login.MainTextWelcome")}</div>
                                <div>{t("login.MainTextLogIn")}</div>
                            </div>
                            <LoginInputForm className="input-form">
                                <InputLabel htmlFor="outlined-adornment-username">{t("login.Username")}</InputLabel>
                                <OutlinedInput
                                    id="outlined-adornment-username"
                                    type="text"
                                    value={loginData.username}
                                    size="small"
                                    label={t("login.Username")}
                                    onChange={(event) =>
                                        setLoginData((prevData) => ({
                                            ...prevData,
                                            username: event.target.value,
                                        }))
                                    }
                                />
                            </LoginInputForm>
                            <LoginButton className="login-button" type="submit">
                                {t("login.Button")}
                            </LoginButton>
                        </form>
                    </div>
                    <div className="login-right-side">
                        <img src={LoginPageIcon} alt="LoginPageIcon" />
                    </div>
                </div>
            </CacheProvider>
        );
    };

    const render = () => {
        if (credentialState === CredentialState.Pending) {
            return <div>{t("login.CheckingYourCredentials")}</div>;
        } else if (credentialState === CredentialState.Error) {
            return <div>Error checking credentials. Please try again.</div>;
        }

        if (authStore.isAuthenticated) {
            const returnUrl = getReturnUrl();
            return <Navigate to={returnUrl}></Navigate>;
        } else {
            return renderForm();
        }
    };

    return render();
};

export default observer(LoginPage);
